import { useState, useEffect, useCallback } from 'react';
import defaultInstance from '../axiosHelper';
import { API_ENDPOINTS } from '../utils/apiEndPoints';

export default function useAreaWiseData(selectedDistrict = null, selectedHealthFacility = null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({
    searchTerm: '',
    sortField: 'district',
    sortOrder: 'asc',
    page: 0,
    rowsPerPage: 10,
  });

  const fetchAreaWiseData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await defaultInstance.get(API_ENDPOINTS.AREA_WISE_DATA);
      if (response?.data?.success) {
        // Filter out rows where district is 'DISTRICT'
        const filteredData = (response.data.data || []).filter(
          item => item.district && item.district.toUpperCase() !== 'DISTRICT'
        );
        setData(filteredData);
      } else {
        throw new Error('Failed to fetch area-wise data');
      }
    } catch (err) {
      console.error('Error fetching area-wise data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAreaWiseData();
  }, [fetchAreaWiseData]);

  // Apply sorting and filtering
  const processedData = useCallback(() => {
    let result = [...data];

      // Apply district filter
    if (selectedDistrict) {
      result = result.filter(item => item.district === selectedDistrict);
    }

    // Apply health facility filter
    if (selectedHealthFacility) {
      result = result.filter(item => item.health_facility === selectedHealthFacility);
    }

    // Apply search filter (only if there's a search term)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.district.toLowerCase().includes(searchLower) ||
          item.health_facility.toLowerCase().includes(searchLower) ||
          (item.village && item.village.toLowerCase().includes(searchLower))
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      const aValue = a[filters.sortField] || '';
      const bValue = b[filters.sortField] || '';

      if (filters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return result;
  }, [data, filters.searchTerm, filters.sortField, filters.sortOrder, selectedDistrict, selectedHealthFacility]);

  // Get paginated data
  const paginatedData = useCallback(() => {
    const startIndex = filters.page * filters.rowsPerPage;
    return processedData().slice(startIndex, startIndex + filters.rowsPerPage);
  }, [processedData, filters.page, filters.rowsPerPage]);

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = filters.sortField === property && filters.sortOrder === 'asc';
    setFilters(prev => ({
      ...prev,
      sortField: property,
      sortOrder: isAsc ? 'desc' : 'asc',
    }));
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setFilters(prev => ({
      ...prev,
      page: newPage,
    }));
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setFilters(prev => ({
      ...prev,
      rowsPerPage: parseInt(event.target.value, 10),
      page: 0, // Reset to first page
    }));
  };

  // Handle search
  const handleSearch = (event) => {
    setFilters(prev => ({
      ...prev,
      searchTerm: event.target.value.toLowerCase(),
      page: 0, // Reset to first page when searching
    }));
  };

  return {
    data: paginatedData(),
    loading,
    error,
    filters: {
      ...filters,
      totalItems: processedData().length,
    },
    handleSort,
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    refetch: fetchAreaWiseData,
  };
}
